import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Card, Grid, IconButton } from "@mui/material"
import { Button, Menu, MenuItem, Skeleton, Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"

import { InfoBlock } from "../../components/containers/InfoBlock"
import { KonanAvatarComponent } from "../../components/core/KonanAvatar"
import { ExportJobList } from "../../types/generated/api/ExportJobList"
import type { ExportJobCardProps } from "./interfaces"

import styles from "./ExportJobs.module.scss"

enum FileTypeEnum {
  MODEL_PREDICTIONS = "Model",
  DEPLOYMENT_PREDICTIONS = "Live",
  SIMULATION_PREDICTIONS = "Simulation",
  WORKFLOW_PREDICTIONS = "Workflow",
}

enum FileTypeColorMap {
  MODEL_PREDICTIONS = "blue",
  DEPLOYMENT_PREDICTIONS = "orange",
  SIMULATION_PREDICTIONS = "pink",
  WORKFLOW_PREDICTIONS = "purple",
}

enum StatusColorMap {
  PENDING = "blue",
  RUNNING = "yellow",
  SUCCEEDED = "green",
  FAILED = "red",
  CANCELLED = "red",
}

/**
 * ExportJob component that wraps the UI for the export jobs
 *
 * @param {ExportJobList} exportJob The export job associated with the data file.
 * @param {function} onDownloadInitiated Callback function called when the download is initiated.
 * @param {Array<object>} [menuOptions] An optional array of menu options.
 * @returns
 */
export function ExportJobCard(props: Readonly<ExportJobCardProps>): React.ReactElement {
  const { exportJob, onDownloadInitiated, menuOptions } = props

  const { search, pathname } = useLocation()

  const { t } = useTranslation("datafiles")

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  return (
    <Card className="card-box-shadow">
      <Grid container direction="column" className={styles.card} gap={1}>
        <Grid container item xs={12} justifyContent={"space-between"}>
          <Grid container item xs={10} gap={1}>
            <Grid item>
              <Tag size="small" color={exportJob?.status && StatusColorMap[exportJob?.status]}>
                {t(`exportJobStatus.${exportJob?.status}`)}
              </Tag>
            </Grid>
            <Grid item>
              <Tag size="small" color={FileTypeColorMap[exportJob.type]}>
                {t(`exportJobtype.${FileTypeEnum[exportJob.type]}`)}
              </Tag>
            </Grid>
            <Grid item>
              <Tag size="small" color="gray">
                {exportJob.data_type}
              </Tag>
            </Grid>
          </Grid>

          {/* Menu  */}
          {menuOptions && menuOptions.length > 0 && (
            <Grid container item xs={2} justifyContent="flex-end" alignItems="flex-start">
              <IconButton
                style={{ marginTop: "-5px" }}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
                aria-label="settings"
                size="small"
              >
                <MoreHorizIcon sx={{ color: "var(--base-text-2)" }} />
              </IconButton>

              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} menuMaxContent>
                {menuOptions.map((option) => (
                  <MenuItem
                    onClick={() => {
                      option.onClick()
                      setAnchorEl(null)
                    }}
                    key={option.title}
                  >
                    <Typography variant="a" color={option.variant ?? "base"} variantColor={2}>
                      {option.title}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          )}
        </Grid>
        {exportJob.status === "SUCCEEDED" ? (
          <Link to={`${pathname}${search}&file_id=${exportJob.uuid}`} className={styles.dataFileName}>
            {/* TODO:: should be handled from SUI, typography tooltip variant without the icon */}
            <Tooltip title={exportJob.name} placement="top" width={"100%"}>
              <Typography variant="h2-bold" className={styles.dataFileName} noWrap color="important" variantColor={2}>
                {exportJob.name}
              </Typography>
            </Tooltip>
          </Link>
        ) : (
          <Tooltip title={exportJob.name} placement="top">
            <Typography variant="h2-bold" noWrap>
              {exportJob.name}
            </Typography>
          </Tooltip>
        )}

        {/* Avatar  */}
        <Grid container item xs={12} justifyContent="space-between" wrap="nowrap">
          <Grid item xs={10}>
            <KonanAvatarComponent
              createdBy={exportJob.created_by}
              title={exportJob.created_by}
              createdAt={exportJob.created_at}
            />
          </Grid>

          {exportJob.status === "SUCCEEDED" && (
            <Grid item display="flex" alignSelf="flex-end" justifySelf="flex-end">
              <Button onClick={() => onDownloadInitiated()} size={"small"} variant="secondary">
                <FileDownloadOutlinedIcon sx={{ color: "var(--base-text-2)", fontSize: "16px" }} />
              </Button>
            </Grid>
          )}
        </Grid>

        {exportJob.status === ExportJobList.status.SUCCEEDED && (
          <Grid item xs={12}>
            <InfoBlock
              text={`${exportJob.predictions_count} / ${exportJob.requested_predictions_count}`}
              title={t("acualPredectedRatio")}
              type="default"
            />
          </Grid>
        )}
      </Grid>
    </Card>
  )
}

/**
 * Loading card for script card
 * @returns {React.ReactElement}
 */
export function ExportJobsLoadingCard(): React.ReactElement {
  return (
    <Card className={"card-box-shadow"}>
      <Grid container direction="column" className={styles.card} gap={1}>
        <Grid item xs={3} mb={0.5}>
          <Skeleton variant="rectangular" height={15} width={"20%"} />
        </Grid>

        <Grid item mt={1} xs={12}>
          <Skeleton variant="rectangular" height={20} width={"60%"} />
        </Grid>

        <Grid item xs={12} container flexWrap="nowrap" spacing={1} alignItems="center">
          <Grid item mt={1}>
            <Skeleton variant="circular" width={32} height={32} />
          </Grid>

          <Grid direction="column" container item mt={1}>
            <Grid item xs={6} my={1}>
              <Skeleton variant="rectangular" height={10} width={"80%"} />
            </Grid>
            <Grid item xs={3} mb={0.5}>
              <Skeleton variant="rectangular" height={10} width={"30%"} />
            </Grid>
          </Grid>
        </Grid>

        <Grid direction="column" container item>
          <Grid item xs={6} my={1}>
            <Skeleton variant="rectangular" height={10} width={"50%"} />
          </Grid>
          <Grid item xs={3} mb={0.5}>
            <Skeleton variant="rectangular" height={28} width={"100%"} />
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
}
